// "Duplicar com novas datas": atalhos de período e o estado que o cadastro recebe. Funções puras.
//
// Datas de Brasília (UTC-3 fixo). O período começa às 00:00 do primeiro dia e termina às 23:59:59 do
// último, com o `-03:00` explícito que o backend espera.

export enum DupPreset {
  weekend = 'weekend',
  week = 'week',
  month = 'month',
}

export interface DupRange {
  /** YYYY-MM-DD (Brasília) */
  from: string
  to: string
  startAt: string
  endAt: string
}

const BR_OFFSET_MS = 3 * 3600 * 1000
const pad = (n: number) => String(n).padStart(2, '0')

const brToday = (now: Date) => {
  const b = new Date(now.getTime() - BR_OFFSET_MS)
  return `${b.getUTCFullYear()}-${pad(b.getUTCMonth() + 1)}-${pad(b.getUTCDate())}`
}

// Aritmética em UTC: dia somado nunca cai no horário de verão de ninguém.
const addDays = (day: string, n: number) => {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}
const weekday = (day: string) => new Date(`${day}T00:00:00Z`).getUTCDay()

export const dupRange = (preset: DupPreset, now: Date = new Date()): DupRange => {
  const today = brToday(now)
  let from = today
  let to = today
  if (preset === DupPreset.weekend) {
    // Próximo sábado depois de hoje: quem duplica no sábado ou no domingo quer o fim de semana seguinte.
    const tomorrow = addDays(today, 1)
    const dow = weekday(tomorrow)
    from = addDays(tomorrow, dow === 6 ? 0 : dow === 0 ? 6 : 6 - dow)
    to = addDays(from, 1)
  } else if (preset === DupPreset.week) {
    to = addDays(today, 6)
  } else {
    to = addDays(today, 29)
  }
  return { from, to, startAt: `${from}T00:00:00-03:00`, endAt: `${to}T23:59:59-03:00` }
}

/**
 * Estado que vai para o cadastro: o próprio registro do GET /discounts/{id} (o cadastro lê postos,
 * valores, limite, público e horários dele) com as datas trocadas, se houver.
 * `overlapsStandardDiscount` e `pointsWhenDiscounting` seguem de forma explícita: a cópia de antes
 * perdia o "Sobrepor o desconto padrão". Notificação e lista de clientes não vêm no GET (só o aviso
 * `hasCustomers`), e é assim mesmo: o cadastro avisa que não são copiadas e pede o público de novo.
 * `datesChosen` diz ao cadastro que o período é o que o dono acabou de escolher, não o da promoção original.
 */
export const buildCloneState = (record: Record<string, any>, overrides: { startAt?: string; endAt?: string } = {}) => ({
  ...record,
  overlapsStandardDiscount: Boolean(record.overlapsStandardDiscount),
  pointsWhenDiscounting: record.pointsWhenDiscounting ?? true,
  ...overrides,
  ...(overrides.startAt ? { datesChosen: true } : {}),
})
